import React, { FC } from 'react'
import {
  addDisplayName,
  addHandlers,
  addEffect,
  addLayoutEffect,
  addMemoBoundary,
  addProps,
  addRef,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { addDebouncedCopy, addIsInitialRender } from 'ad-hok-utils'
import {
  FeedMedia,
  isImageFeedContent,
  ProductWithImage,
  videoThumbnail,
} from 'shared/src/api/types'
import { typedAs } from 'shared/src/utils/typedAs'
import { addApi } from 'shared/src/api'
import { trackEvent, trackMediaView } from 'shared/src/utils/analytics'
import { addUpdateCurrentHistoryEntry } from 'shared/src/utils/addNavigate'
import { sortBy } from 'lodash'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Container } from './Container'
import { Video } from '../video'
import { Creator } from '../../Creator'
import { Lasso, PRODUCTS_PER_LASSO } from './rodeo/Lasso'
import { animationDuration, animationTimingFunction } from './rodeo/animation'
import { sansSerifFontFamily } from '../../../utils/styles'
import { CREATOR_AVATAR_Z_INDEX } from '../../../utils/zIndex'
import { makeFluidCreatorUrl } from 'utils/routes'
import verifiedBadge from '../../../assets/verified-badge.svg'
import { LinkOrButton } from 'components/LinkOrButton'
import { addFeedContext } from '../context'

export type MediaState = 'feed' | 'rodeo'

export const invertState = (state: MediaState): MediaState =>
  state === 'feed' ? 'rodeo' : 'feed'

// Returns an array of products, reordered if necessary, to make sure
// that search matches appear in the first "lasso" of products, when
// possible.
const reorderedProductsForMatches = (
  products: ProductWithImage[]
): ProductWithImage[] => {
  const indexOfFirstMatch = [...products]
    .reverse()
    .findIndex((product) => product.matchesSearch)

  if (
    products.length > PRODUCTS_PER_LASSO &&
    indexOfFirstMatch >= 0 &&
    indexOfFirstMatch < PRODUCTS_PER_LASSO
  ) {
    return sortBy(products, (product) => !product.matchesSearch)
  } else {
    return products
  }
}

interface StyleProps {
  state: MediaState
  captionExpanded: boolean
  isAnimating: boolean
}

const cssPropIfRodeo =
  (value: any, elseValue?: any) =>
  ({ state }: StyleProps) =>
    state === 'rodeo' ? value : elseValue

const cssPropIfFeedAndCaptionExpanded =
  (value: any, elseValue?: any) =>
  ({ state, captionExpanded }: StyleProps) =>
    state === 'feed' && captionExpanded === true ? value : elseValue

const animationName =
  (name: string) =>
  ({ isAnimating }: StyleProps) =>
    isAnimating ? name : undefined

const animationDirection = cssPropIfRodeo('normal', 'reverse')

const useStyles = createUseStyles({
  absoluteCenter: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageContainer: {
    border: 'none',
    backgroundColor: 'transparent',
    position: 'relative',
    width: '100%',
    maxHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
  },
  image: {
    width: cssPropIfRodeo('50%', '100%'),
    height: '100%',
    objectFit: 'contain',
    margin: 0,
    opacity: cssPropIfRodeo('60%', '100%'),
    transition: 'width 0.4s ease-in-out, opacity 0.4s ease-in-out',
  },
  video: {
    width: cssPropIfRodeo('50%', '100%'),
    height: '100%',
    objectFit: 'cover',
    opacity: cssPropIfRodeo('60%', '100%'),
    transition: 'width 0.4s ease-in-out, opacity 0.4s ease-in-out',
  },
  gradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    background:
      'linear-gradient(black, transparent 7%, transparent 93%, black)',
    pointerEvents: 'none',
  },
  footerContainerHolder: {
    background: 'linear-gradient(transparent, #00000059 10%, black)',
    width: '100%',
    bottom: '36px',
    zIndex: '1',
    position: 'absolute',
    alignItems: 'center',
    flexDirection: 'row',
    paddingTop: '30px',
  },
  footerContainer: {
    paddingLeft: 12,
    marginRight: 96,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    zIndex: CREATOR_AVATAR_Z_INDEX,
  },
  footerText: {
    paddingLeft: 12,
    fontFamily: sansSerifFontFamily,
    fontWeight: 600,
    color: 'white',
  },
  username: {
    fontSize: 14,
    display: '-webkit-box',
    '-webkit-line-clamp': 1,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  verifiedBadge: {
    position: 'absolute',
    height: 16,
    paddingLeft: 4,
  },
  caption: {
    marginTop: 5,
    fontSize: 12,
    animationDirection,
    animationDuration,
    animationTimingFunction,
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    '-webkit-line-clamp': cssPropIfFeedAndCaptionExpanded(15, 1),
    animationName: animationName('$captionOut'),
    transition: 'all 0.3s ease-in-out',
    lineHeight: '16px',
    maxHeight: cssPropIfFeedAndCaptionExpanded('300px', '16px'),
    cursor: 'pointer',
  },
  '@keyframes captionOut': {
    from: {
      '-webkit-line-clamp': 15,
    },
    to: {
      '-webkit-line-clamp': 1,
    },
  },
  barGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 80,
    background: 'linear-gradient(black, transparent)',
    opacity: cssPropIfRodeo('100%', 0),
    animationName: animationName('$barGradientIn'),
    animationDirection,
    animationDuration,
    animationTimingFunction,
  },
  '@keyframes barGradientIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: '100%',
    },
  },
})

interface Props {
  media: FeedMedia
  state: MediaState
  onToggleState: () => void
  onClickCreator?: () => void
  className?: string
  isFlipped?: boolean
  scrollableContainerElement: HTMLDivElement | null
}

export const Media: FC<Props> = flowMax(
  addDisplayName('Media'),
  addState('isWithinVisibilityWindow', 'setWithinVisibilityWindow', false),
  addState('isVisible', 'setVisible', false),
  addState('captionExpanded', 'setCaptionExpanded', false),
  addMemoBoundary([
    'media',
    'state',
    'onToggleState',
    'onClickCreator',
    'className',
    'isFlipped',
    'isWithinVisibilityWindow',
    'isVisible',
    'navigateToProfile',
    'scrollableContainerElement',
    'captionExpanded',
  ]),
  addState('isAnimating', 'setAnimating', false),
  addIsInitialRender,
  // Using the caption element as a proxy for all animated elements,
  // since they all share the same duration and timing function
  addRef('captionRef', typedAs<HTMLParagraphElement | null>(null)),
  addLayoutEffect(
    ({ setAnimating, isInitialRender, captionRef }) =>
      () => {
        const caption = captionRef.current

        if (!isInitialRender && caption) {
          setAnimating(true)

          caption.onanimationend = () => {
            caption.onanimationend = null
            setAnimating(false)
          }
        }

        return () => {
          if (captionRef.current) {
            captionRef.current.onanimationend = null
          }
        }
      },
    ['state']
  ),
  addHandlers({
    onToggleState:
      ({ onToggleState, state, media }) =>
      () => {
        if (state === 'feed') {
          trackEvent(
            {
              category: 'media',
              action: 'show_rodeo',
            },
            {
              media_id: media.id,
              creator_id: media.user.id,
            }
          )
        }

        onToggleState()
      },
  }),
  addProps(({ media: { products } }) => ({
    numProductGroups: Math.ceil(products.length / PRODUCTS_PER_LASSO),
    products: reorderedProductsForMatches(products),
  })),
  addStateHandlers(
    {
      productGroup: 0,
    },
    {
      onShuffle:
        ({ productGroup }, { numProductGroups, media }) =>
        () => {
          const nextGroup = (productGroup + 1) % numProductGroups

          trackEvent(
            {
              category: 'media',
              action: 'show_more_products',
            },
            {
              media_id: media.id,
              creator_id: media.user.id,
            }
          )

          return {
            productGroup: nextGroup,
          }
        },
    }
  ),
  addProps(({ products, productGroup, numProductGroups, onShuffle }) => {
    const nextGroup = (productGroup + 1) % numProductGroups
    const productsInNextGroup = products.slice(
      nextGroup * PRODUCTS_PER_LASSO,
      nextGroup * PRODUCTS_PER_LASSO + PRODUCTS_PER_LASSO
    ).length

    return {
      products: products.slice(
        productGroup * PRODUCTS_PER_LASSO,
        productGroup * PRODUCTS_PER_LASSO + PRODUCTS_PER_LASSO
      ),
      shuffle:
        products.length > PRODUCTS_PER_LASSO
          ? {
              onClick: onShuffle,
              value: `+${productsInNextGroup}`,
            }
          : undefined,
    }
  }),
  addState(
    'containerElement',
    'setContainerElement',
    typedAs<HTMLDivElement | null>(null)
  ),
  addUpdateCurrentHistoryEntry,
  addFeedContext,
  addEffect(
    ({
        media: { id },
        scrollableContainerElement,
        containerElement,
        setWithinVisibilityWindow,
        setVisible,
        setVisibleMediaId,
        updateCurrentHistoryEntry,
      }) =>
      () => {
        if (!scrollableContainerElement || !containerElement) {
          return
        }

        // Observe whether the media is within the visibility window
        // (currently visible, or withing 2 media on either side of
        // the visible media)
        const visibilityWindowObserver = new IntersectionObserver(
          (entries) => {
            const isInVisibilityWindow = entries.some(
              (entry) => entry.isIntersecting
            )
            setWithinVisibilityWindow(isInVisibilityWindow)
          },
          {
            root: scrollableContainerElement,
            rootMargin: '200% 0px 200% 0px',
            threshold: 0,
          }
        )
        visibilityWindowObserver.observe(containerElement)

        // Observe whether the media is visible
        const visibilityObserver = new IntersectionObserver(
          (entries) => {
            const isVisible = entries.some((entry) => entry.isIntersecting)
            setVisible(isVisible)

            if (isVisible) {
              setVisibleMediaId(id)
              updateCurrentHistoryEntry({ hash: id.toString() })
            }
          },
          {
            threshold: 0.51,
          }
        )
        visibilityObserver.observe(containerElement)

        return () => {
          visibilityWindowObserver.disconnect()
          visibilityObserver.disconnect()
        }
      },
    ['scrollableContainerElement', 'containerElement']
  ),
  addDebouncedCopy(500, 'isVisible', 'isActive'),
  addApi,
  addEffect(
    ({
        media: {
          id,
          user: { id: creatorId },
        },
        isActive,
        api,
      }) =>
      () => {
        if (!isActive) {
          return
        }

        trackMediaView(id, creatorId, api)
      },
    ['isActive']
  ),
  addProps(({ isAnimating, state, captionExpanded }) => ({
    classes: useStyles({ isAnimating, state, captionExpanded }),
  })),
  addTranslationHelpers,
  ({
    media,
    media: { user, caption, content },
    products,
    state,
    onToggleState,
    onClickCreator,
    setContainerElement,
    captionExpanded,
    setCaptionExpanded,
    captionRef,
    isAnimating,
    isFlipped,
    shuffle,
    classes,
    className,
    isWithinVisibilityWindow,
    isActive,
    t,
  }) => (
    <Container
      isVisible={isWithinVisibilityWindow}
      creator={user}
      media={media}
      state={state}
      onToggleProducts={onToggleState}
      productsActive={state === 'rodeo'}
      innerRef={setContainerElement}
      className={className}
    >
      <div className={classes.absoluteCenter}>
        <button
          onClick={isImageFeedContent(content) ? onToggleState : undefined}
          className={classes.imageContainer}
        >
          {isImageFeedContent(content) ? (
            <img
              src={
                isImageFeedContent(content)
                  ? content.value.full
                  : videoThumbnail(content)
              }
              alt=""
              onClick={state === 'rodeo' ? onToggleState : undefined}
              className={classes.image}
            />
          ) : (
            <Video
              video={content}
              isActive={isActive}
              className={classes.video}
            />
          )}
          <div className={classes.gradient} />
        </button>
      </div>
      <React.Fragment key={state}>
        <div className={classes.footerContainerHolder}>
          <div className={classes.footerContainer}>
            <Creator
              creator={user}
              height={40}
              shadow
              onClick={onClickCreator}
            />
            <div className={classes.footerText}>
              <LinkOrButton
                navigateTo={makeFluidCreatorUrl(user.username)}
                onClick={onClickCreator}
                showBackButton
                replace={!!onClickCreator}
                className={classes.username}
              >
                <>
                  {user.username}
                  {user.isVerified && (
                    <img
                      src={verifiedBadge}
                      alt={t('profile.verified')}
                      className={classes.verifiedBadge}
                    />
                  )}
                </>
              </LinkOrButton>
              <div
                className={classes.caption}
                ref={captionRef}
                onClick={() => setCaptionExpanded(!captionExpanded)}
              >
                {caption}
              </div>
            </div>
          </div>
        </div>
        {(state === 'rodeo' || isAnimating) && (
          <>
            <Lasso
              origin={{
                user,
                mediaId: media.id,
              }}
              products={products}
              layout="media"
              animate={isAnimating}
              animationDirection={state === 'rodeo' ? 'normal' : 'reverse'}
              className={classes.absoluteCenter}
              showBookmarks
              isFlipped={isFlipped}
              shuffle={shuffle}
            />
            <div className={classes.barGradient} />
          </>
        )}
      </React.Fragment>
    </Container>
  )
)
